/**
 * PreviewRenderer Component
 * =========================
 * Universal renderer for slot-based page layouts.
 * Works for both preview mode (in builder) and production runtime.
 * 
 * Features:
 * - Unified slot-based rendering for all layouts
 * - SlotPlaceholder for empty slots with guidance
 * - Component action execution support
 * - Responsive layout rendering
 * - Drag-drop visual feedback
 * - Production-ready output
 * 
 * @component
 */

import React, { useState, useEffect } from 'react';
import {
  TextPreview,
  ButtonPreview,
  CardPreview,
  ImagePreview,
  InputPreview,
  SelectPreview,
  CheckboxPreview,
  CustomPreview,
  ParagraphPreview,
  HeaderPreview,
  TextareaPreview,
  AutocompletePreview,
  RadioGroupPreview,
  SwitchPreview,
  NumberPreview,
  FileUploaderPreview,
  ListPreview,
  AccordionPreview,
  TabsPreview,
  ModalPreview,
  VideoPreview,
  AudioPreview,
  CarouselPreview,
  MapPreview,
  TablePreview,
  ChartPreview,
  ProgressBarPreview,
  BadgePreview,
  AvatarPreview,
  AlertPreview,
  TooltipPreview,
  PopoverPreview,
  BreadcrumbPreview,
  PaginationPreview,
  NavigationPreview,


  HeroSectionPreview,
  LinkPreview,
 
  FooterPreview,

  GridPreview


} from '../../PreviewComponents';
import UniversalFormPreview from '../../FormBuilder/UniversalFormPreview';
import { getLayout, getLayoutSlots, getResponsiveLayoutGap, getResponsiveLayoutPadding } from '../../../config/layouts';
import { getTheme } from '../../../config/themes';
import { getSlotDefinition, getSlotInternalSpacing } from '../../../config/slotDefinitions';
import SlotPlaceholder from './SlotPlaceholder';
import { executeAction } from '../../../utils/actionExecutor';
import { fetchPageByName } from '../../../services/firestoreService';
import { useAppStore } from '../../../store/useAppStore';
import { getDefaultCommonAttrs, getDefaultSpecificAttrs } from '../../../utils/defaultAttributes';

// Component mapping for preview/production
const componentMap = {
  text: TextPreview,
  button: ButtonPreview,
  card: CardPreview,
  image: ImagePreview,
  input: InputPreview,
  select: SelectPreview,
  checkbox: CheckboxPreview,
  custom: CustomPreview,
  paragraph: ParagraphPreview,
  header: HeaderPreview,
  textarea: TextareaPreview,
  autocomplete: AutocompletePreview,
  radioGroup: RadioGroupPreview,
  switch: SwitchPreview,
  number: NumberPreview,
  fileUploader: FileUploaderPreview,
  form: UniversalFormPreview,
  list: ListPreview,
  accordion: AccordionPreview,
  tabs: TabsPreview,
  modal: ModalPreview,
  video: VideoPreview,
  audio: AudioPreview,
  carousel: CarouselPreview,
  map: MapPreview,
  table: TablePreview,
  chart: ChartPreview,
  progressBar: ProgressBarPreview,
  badge: BadgePreview,
  avatar: AvatarPreview,
  alert: AlertPreview,
  tooltip: TooltipPreview,
  popover: PopoverPreview,
  breadcrumb: BreadcrumbPreview,
  pagination: PaginationPreview,
  navigation: NavigationPreview,


  heroSection: HeroSectionPreview,
  link: LinkPreview,
 
  footer: FooterPreview,

  grid: GridPreview


};

// Inline action runner (supports both legacy and new action schema)
const runActions = async (actions, context) => {
  const results = [];

  for (const action of actions) {
    try {
      // New Action Configuration System format:
      // { actionId, actionJSON, triggerEvent }
      if (action?.actionJSON?.actionType) {
        const ok = await executeAction(action.actionJSON);
        results.push({ success: Boolean(ok), action: action.actionJSON.actionType });
        continue;
      }

      // Also accept raw action config objects as actionJSON-less entries
      if (action?.actionType) {
        const ok = await executeAction(action);
        results.push({ success: Boolean(ok), action: action.actionType });
        continue;
      }

      // Legacy format (kept for backward compatibility)
      if (action?.type === "ShowMessage") {
        alert(action.message || "Action executed");
        results.push({ success: true, action: action.type });
        continue;
      }
      if (action?.type === "NavigateTo") {
        console.log(`[ActionRunner] Navigate to: ${action.url}`);
        results.push({ success: true, action: action.type });
        continue;
      }

      console.log(`[ActionRunner] Executed (unknown schema):`, action, context);
      results.push({ success: true, action: action?.type || action?.actionType || 'unknown' });
    } catch (error) {
      console.error(`[ActionRunner] Failed`, error);
      results.push({ success: false, action: action?.type || action?.actionType || 'unknown', error });
    }
  }

  return {
    success: results.every(r => r.success),
    results
  };
};

// fixed/updated by ChatGPT on 2026-01-25 17:45:00 – if a component is placed inside a Grid cell, do not also render it as a standalone slot component
const getGridReferencedComponentIds = (slotComponents = []) => {
  const ids = new Set();

  slotComponents.forEach((comp) => {
    if (comp?.type === 'grid') {
      const cells = comp?.specificAttrs?.cells || [];
      cells.forEach((cell) => {
        if (cell?.componentRefId) ids.add(cell.componentRefId);
      });
    }
  });

  return ids;
};


const PreviewRenderer = ({ 
  components, 
  layoutId, 
  themeId, 
  onComponentClick, 
  selectedComponentId,
  onAddComponentToSlot, // Callback when user clicks to add component to slot
  isProductionMode = false, // Toggle between preview and production rendering
  onUpdateComponents // Optional callback to update components (for slot navigation)
}) => {
  const layout = getLayout(layoutId);
  const theme = getTheme(themeId);
  const slots = getLayoutSlots(layoutId);
  const { appName } = useAppStore();
  
  // State to track slot-specific component overrides (for slot navigation)
  const [slotComponentOverrides, setSlotComponentOverrides] = useState({});
  
  // Responsive spacing state - updates on window resize
  const [layoutGap, setLayoutGap] = useState(() => getResponsiveLayoutGap(layoutId));
  const [layoutPadding, setLayoutPadding] = useState(() => getResponsiveLayoutPadding(layoutId));

  // Update spacing on window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setLayoutGap(getResponsiveLayoutGap(layoutId));
      setLayoutPadding(getResponsiveLayoutPadding(layoutId));
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [layoutId]);

  // Listen for slot navigation events
  useEffect(() => {
    const handleSlotNavigation = async (event) => {
      const { targetPage, targetSlotId, targetSlot, clearSlot } = event.detail || {};
      const resolvedSlotId = targetSlotId || targetSlot;
      
      if (!targetPage || !resolvedSlotId || !appName) {
        console.warn('⚠️ [PreviewRenderer] Slot navigation missing required params:', { 
          targetPage, 
          targetSlotId, 
          targetSlot,
          resolvedSlotId,
          appName 
        });
        return;
      }

      console.log('🔗 [PreviewRenderer] Slot navigation requested', { 
        targetPage, 
        targetSlotId, 
        targetSlot,
        resolvedSlotId,
        clearSlot 
      });

      try {
        // Fetch target page
        const targetPageData = await fetchPageByName(appName, targetPage);
        if (!targetPageData) {
          console.error('❌ [PreviewRenderer] Target page not found:', targetPage);
          return;
        }

        // Get layout slots to validate slot ID
        const layoutSlots = getLayoutSlots(layoutId);
        const validSlotIds = layoutSlots.map(s => s.id);
        
        // Resolve slot ID - check if it exists in current layout, otherwise use main content slot
        let finalSlotId = resolvedSlotId;
        if (!validSlotIds.includes(resolvedSlotId)) {
          // Try to find a matching slot by name or use default
          const matchingSlot = layoutSlots.find(s => 
            s.id === resolvedSlotId || 
            s.name?.toLowerCase() === resolvedSlotId.toLowerCase() ||
            (resolvedSlotId === 'body' && (s.id === 'content' || s.id === 'body')) ||
            (resolvedSlotId === 'content' && (s.id === 'content' || s.id === 'body'))
          );
          finalSlotId = matchingSlot?.id || layoutSlots.find(s => s.id === 'content' || s.id === 'body')?.id || layoutSlots[0]?.id || 'body';
          console.log('🔄 [PreviewRenderer] Resolved slot ID', { 
            requested: resolvedSlotId, 
            resolved: finalSlotId 
          });
        }

        // Extract components for the target slot
        const allComponents = targetPageData.components || [];
        
        // First, try to filter by exact slotId match
        let slotComponents = allComponents
          .filter(comp => {
            const compSlotId = comp.slotId || finalSlotId;
            // Match by exact slot ID or by matching slot names
            return compSlotId === finalSlotId || 
                   compSlotId === resolvedSlotId ||
                   (compSlotId === 'body' && finalSlotId === 'content') ||
                   (compSlotId === 'content' && finalSlotId === 'body');
          });
        
        // If no components matched, include ALL components from the page
        // This handles cases where components don't have slotId set or use different slot names
        if (slotComponents.length === 0 && allComponents.length > 0) {
          console.log('⚠️ [PreviewRenderer] No components matched slot, including all components from page', {
            requestedSlot: resolvedSlotId,
            finalSlotId,
            totalComponents: allComponents.length,
            componentTypes: allComponents.map(c => c.type),
            componentSlotIds: allComponents.map(c => c.slotId)
          });
          slotComponents = allComponents;
        }
        
        // Map components and ensure slotId is set correctly
        slotComponents = slotComponents.map(comp => ({
          ...comp,
          slotId: finalSlotId // Ensure slotId is set correctly for target slot
        }));

        console.log('✅ [PreviewRenderer] Found components for slot', { 
          requestedSlot: resolvedSlotId,
          finalSlotId,
          componentCount: slotComponents.length,
          componentTypes: slotComponents.map(c => c.type)
        });

        // Update slot component overrides
        setSlotComponentOverrides(prev => {
          const updated = { ...prev };
          if (clearSlot !== false) {
            // Clear slot before loading - replace all components in this slot
            updated[finalSlotId] = slotComponents;
          } else {
            // Append to existing content
            // Get base components for this slot (from props) + any previous overrides
            const baseComponentsForSlot = (components || []).filter(comp => {
              const compSlotId = comp.slotId;
              return compSlotId === finalSlotId;
            });
            const previousOverrides = prev[finalSlotId] || [];
            // Use previous overrides if they exist, otherwise use base components
            const existing = previousOverrides.length > 0 ? previousOverrides : baseComponentsForSlot;
            updated[finalSlotId] = [...existing, ...slotComponents];
          }
          return updated;
        });

        // If callback provided, also update parent component state
        if (onUpdateComponents) {
          const currentComponents = components || [];
          const otherSlotComponents = currentComponents.filter(comp => {
            const compSlotId = comp.slotId || finalSlotId;
            return compSlotId !== finalSlotId;
          });
          
          const updatedComponents = clearSlot !== false
            ? [...otherSlotComponents, ...slotComponents]
            : [...currentComponents, ...slotComponents];
          
          onUpdateComponents(updatedComponents);
        }

        console.log('✅ [PreviewRenderer] Slot updated', { 
          requestedSlot: resolvedSlotId,
          finalSlotId, 
          clearSlot,
          componentCount: slotComponents.length
        });
      } catch (error) {
        console.error('❌ [PreviewRenderer] Slot navigation failed:', error);
      }
    };

    window.addEventListener('slotNavigation', handleSlotNavigation);
    return () => window.removeEventListener('slotNavigation', handleSlotNavigation);
  }, [appName, components, onUpdateComponents, layoutId]);

  // fixed/updated by ChatGPT on 2026-01-25 18:05:00 – collect all component ids referenced by any grid cell so we can avoid rendering them twice
function collectAllGridReferencedIds(allComps = []) {
  const ids = new Set();
  (allComps || []).forEach((comp) => {
    if (comp?.type === 'grid') {
      const cells = comp?.specificAttrs?.cells || [];
      cells.forEach((cell) => {
        if (cell?.componentRefId) ids.add(cell.componentRefId);
      });
    }
  });
  return ids;
}


  // Organize components by slot ID
  function organizeComponentsBySlot() {
    const slotMap = {};
    
    // Initialize empty arrays for each slot
    slots.forEach(slot => {
      slotMap[slot.id] = [];
    });
    
    // Start with base components
    let mergedComponents = [...(components || [])];
    
    // Apply slot-specific overrides (from slot navigation)
    // Only override components that belong to slots that have been navigated to
    Object.keys(slotComponentOverrides).forEach(slotId => {
      // Remove ONLY components that belong to this specific slot
      mergedComponents = mergedComponents.filter(comp => {
        const compSlotId = comp.slotId;
        // Only filter out if it's the exact slot we're overriding
        return compSlotId !== slotId;
      });
      // Add override components for this slot
      const overrideComps = slotComponentOverrides[slotId] || [];
      mergedComponents = [...mergedComponents, ...overrideComps];
    });
    
    // fixed/updated by ChatGPT on 2026-01-25 18:05:00 – prevent components referenced inside Grid from rendering as standalone components
    const gridReferencedIds = collectAllGridReferencedIds(mergedComponents);

    // fixed/updated by ChatGPT on 2026-01-25 17:45:00 – do not slot-render nested components (grid children)
    mergedComponents = mergedComponents.filter(c => !c?.isNested && !c?.parentComponentId);

    // Assign components to their slots
    mergedComponents.forEach(component => {
      // Skip referenced child components (they must render ONLY inside the grid cell)
      if (component?.type !== 'grid' && gridReferencedIds.has(component?.id)) {
        return;
      }

      const slotId = component.slotId;
      if (slotId) {
        // Try to find slot by ID first
        if (slotMap[slotId]) {
          slotMap[slotId].push(component);
        } else {
          // Try to find slot by gridArea (for compatibility)
          const slotByArea = slots.find(s => s.gridArea === slotId);
          if (slotByArea && slotMap[slotByArea.id]) {
            slotMap[slotByArea.id].push(component);
          } else {
            // Try common slot name mappings
            const slotMapping = {
              'body': 'content',
              'content': 'content',
              'main': 'main-content',
              'main-content': 'main-content',
              'sidebar': 'sidebar',
              'header': 'header',
              'footer': 'footer'
            };
            const mappedSlotId = slotMapping[slotId];
            if (mappedSlotId && slotMap[mappedSlotId]) {
              slotMap[mappedSlotId].push(component);
            } else {
              // Unassigned components go to first slot with allowMultiple or first required slot
              const flexSlot = slots.find(s => s.allowMultiple) || slots.find(s => s.required) || slots[0];
              if (flexSlot && slotMap[flexSlot.id]) {
                slotMap[flexSlot.id].push(component);
              }
            }
          }
        }
      } else {
        // Unassigned components go to first slot with allowMultiple or first required slot
        const flexSlot = slots.find(s => s.allowMultiple) || slots.find(s => s.required) || slots[0];
        if (flexSlot && slotMap[flexSlot.id]) {
          slotMap[flexSlot.id].push(component);
        }
      }
    });

    return slotMap;
  }

  const componentsBySlot = organizeComponentsBySlot();

  // Handle component click - selection + action execution
  async function handleComponentClick(component) {
    if (!isProductionMode && onComponentClick) {
      onComponentClick(component.id);
    }

    // Execute configured actions if present
    const actions = component.actions || [];
    if (!actions.length) return;

    try {
      // Only run actions configured for onClick (new schema)
      const clickActions = actions.filter(a => !a?.triggerEvent || a.triggerEvent === 'onClick');

      await runActions(clickActions, {
        formData: {},
        services: {
          toast: { show: (msg) => alert(msg) }
        },
        env: { appName: 'current-app' },
        meta: { 
          pageId: 'current-page', 
          componentId: component.id, 
          componentType: component.type 
        }
      });
    } catch (error) {
      console.error('[PreviewRenderer] Action execution failed:', error);
    }
  }

  const normalizeComponentForPreview = (comp) => {
    if (!comp) return comp;

    const next = { ...comp };

    if (!next.commonAttrs) {
      next.commonAttrs = getDefaultCommonAttrs();
    }

    if (!next.specificAttrs) {
      next.specificAttrs = getDefaultSpecificAttrs(next.type, theme);
    }

    return next;
  };

  // Render a single component
  function renderComponent(component) {
    const safeComponent = normalizeComponentForPreview(component);

  const PreviewComponent = componentMap[safeComponent.type];
    
    if (!PreviewComponent) {
      return (
        <div key={component.id} style={{
          padding: '16px',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FCA5A5',
          borderRadius: '6px',
          marginBottom: '8px',
          fontSize: '14px',
          color: '#991B1B'
        }}>
          Unknown component type: {component.type}
        </div>
      );
    }

    return (
      <PreviewComponent
        key={component.id}
        component={component}
        theme={theme}
        runtime={isProductionMode}
        onClick={() => handleComponentClick(component)}
        isSelected={!isProductionMode && selectedComponentId === component.id}
        allComponents={components || []}
        renderChild={(childComponent) => renderComponent(childComponent)}
      />
    );
  }

  // Container styles with responsive padding
  const containerStyle = {
    backgroundColor: theme.colors.bg,
    borderRadius: isProductionMode ? 0 : '12px',
    padding: isProductionMode ? 0 : `${layoutPadding}px`,
    minHeight: '400px',
    boxShadow: isProductionMode ? 'none' : '0 4px 6px rgba(0, 0, 0, 0.05)',
    fontFamily: theme.fonts.body,
    color: theme.colors.text,
    width: '100%'
  };

  // Render slot-based layout
  function renderSlotBasedLayout() {
    if (!layout || !layout.gridClass) {
      return (
        <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>
          <p>Layout configuration not found.</p>
        </div>
      );
    }

    return (
      <div 
        className={layout.gridClass} 
        style={{ 
          display: 'grid',
          gap: `${layoutGap}px`, // Apply responsive layout-level gap
          width: '100%',
          minHeight: '100%',
          // Ensure grid template areas from CSS are applied
          // The CSS class should provide grid-template-columns and grid-template-areas
        }}
      >
        {slots.map((slot, index) => {
          const slotComponents = componentsBySlot[slot.id] || [];
          // fixed/updated by ChatGPT on 2026-01-25 17:45:00 – hide components that are referenced by a Grid, so they render only inside the Grid cell
          const gridReferencedIds = getGridReferencedComponentIds(slotComponents);

          // NOTE: keep grids themselves visible; hide only referenced children
          const visibleSlotComponents = slotComponents.filter((c) => !gridReferencedIds.has(c.id));

          const slotDefinition = getSlotDefinition(slot.id);
          const isEmpty = visibleSlotComponents.length === 0;
          const isRequired = slot.required;
          
          // Get slot-specific internal spacing (gap between components within this slot)
          const slotInternalGap = getSlotInternalSpacing(slot.id);

          return (
            <div
              key={slot.id}
              data-slot-id={slot.id}
              {...(!isProductionMode && {
                'data-slot-index': index + 1
              })}
              style={{
                gridArea: slot.gridArea,
                minHeight: isEmpty && !isProductionMode ? '100px' : 'auto',
                position: 'relative',
                border: !isProductionMode && isEmpty && isRequired 
                  ? '2px dashed #F59E0B' 
                  : 'none',
                borderRadius: '8px',
                padding: isEmpty && !isProductionMode ? '8px' : 0
              }}
            >
              {/* Empty slot placeholder (preview mode only) */}
              {isEmpty && !isProductionMode ? (
                <SlotPlaceholder
                  slot={slot}
                  layoutId={layoutId}
                  slotDefinition={slotDefinition}
                  onAddComponent={onAddComponentToSlot}
                />
              ) : isEmpty && isProductionMode ? (
                // Production mode: render nothing for empty slots
                null
              ) : slotDefinition?.gridDisplay ? (
                // Special rendering for gallery/grid slots
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                  gap: `${slotInternalGap}px`, // Use slot-specific internal gap for grid
                  width: '100%'
                }}>
                  {visibleSlotComponents.map(component => renderComponent(component))}
                </div>
              ) : (
                // Render slot components with direction support and proper spacing
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: `${slotInternalGap}px`, // Use slot-specific internal gap
                  width: '100%'
                }}>
                  {/* Group consecutive components by direction and render */}
                  {(() => {
                    const groups = [];
                    let currentGroup = [];
                    let currentDirection = null;

                    slotComponents.forEach((component, idx) => {
                      const compDirection = component.direction || 'column';
                      
                      if (currentDirection === null || currentDirection === compDirection) {
                        currentDirection = compDirection;
                        currentGroup.push(component);
                      } else {
                        groups.push({ direction: currentDirection, components: currentGroup });
                        currentDirection = compDirection;
                        currentGroup = [component];
                      }
                      
                      // Push last group
                      if (idx === slotComponents.length - 1) {
                        groups.push({ direction: currentDirection, components: currentGroup });
                      }
                    });

                    return groups.map((group, groupIdx) => (
                      <div
                        key={groupIdx}
                        style={{
                          display: 'flex',
                          flexDirection: group.direction === 'row' ? 'row' : 'column',
                          gap: `${slotInternalGap}px`, // Use slot-specific internal gap for all directions
                          width: '100%',
                          flexWrap: group.direction === 'row' ? 'wrap' : 'nowrap'
                        }}
                      >
                        {group.components.map(component => renderComponent(component))}
                      </div>
                    ));
                  })()}
                  
                  {/* Add another button (preview mode only) */}
                  {!isProductionMode && slot.allowMultiple && 
                   slotComponents.length < (slot.maxComponents || 999) && onAddComponentToSlot && (
                    <button
                      type="button"
                      onClick={() => onAddComponentToSlot({
                        slotId: slot.id,
                        slotName: slot.name,
                        suggestedComponents: slot.suggestedComponents || []
                      })}
                      style={{
                        padding: '10px',
                        backgroundColor: '#F3F4F6',
                        border: '1px dashed #D1D5DB',
                        borderRadius: '6px',
                        color: '#6B7280',
                        fontSize: '12px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#E5E7EB';
                        e.target.style.borderColor = '#9CA3AF';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#F3F4F6';
                        e.target.style.borderColor = '#D1D5DB';
                      }}
                    >
                      ➕ Add Another to {slot.name}
                    </button>
                  )}
                </div>
              )}

              {/* Slot number badge (preview mode only, for easier identification) */}
              {!isProductionMode && (
                <div
                  style={{
                    position: 'absolute',
                    top: '4px',
                    left: '4px',
                    width: '24px',
                    height: '24px',
                    borderRadius: '50%',
                    backgroundColor: isEmpty ? '#F3F4F6' : '#3B82F6',
                    color: isEmpty ? '#6B7280' : '#FFFFFF',
                    fontSize: '11px',
                    fontWeight: '700',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    zIndex: 10,
                    pointerEvents: 'none'
                  }}
                >
                  {index + 1}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // Main render
  return (
    <div style={containerStyle}>
      {slots.length === 0 ? (
        // Fallback for layouts without slots (shouldn't happen now)
        <div style={{
          padding: '40px',
          textAlign: 'center',
          color: '#9CA3AF'
        }}>
          <p>This layout has no slots defined.</p>
          <p style={{ fontSize: '12px', marginTop: '8px' }}>
            Please select a different layout or contact support.
          </p>
        </div>
      ) : (
        // Render slot-based layout
        renderSlotBasedLayout()
      )}
    </div>
  );
};

export default PreviewRenderer;